import * as React from "react"
import ReactDOM from "react-dom"
import { X } from "lucide-react"
import { cn } from "@/lib/utils"

// Dialog Context
const DialogContext = React.createContext(null)

const Dialog = ({ open, onOpenChange, children }) => {
  return (
    <DialogContext.Provider value={{ open, onOpenChange }}>
      {children}
    </DialogContext.Provider>
  )
}

const DialogTrigger = React.forwardRef(({ children, asChild, ...props }, ref) => {
  const { onOpenChange } = React.useContext(DialogContext)
  
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...props,
      ref,
      onClick: (e) => {
        children.props.onClick?.(e)
        onOpenChange?.(true)
      }
    })
  }
  
  return (
    <button ref={ref} onClick={() => onOpenChange?.(true)} {...props}>
      {children}
    </button>
  )
})
DialogTrigger.displayName = "DialogTrigger"

const DialogClose = React.forwardRef(({ children, asChild, ...props }, ref) => {
  const { onOpenChange } = React.useContext(DialogContext)
  
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(children, {
      ...props,
      ref,
      onClick: (e) => {
        children.props.onClick?.(e)
        onOpenChange?.(false)
      }
    })
  }
  
  return (
    <button ref={ref} onClick={() => onOpenChange?.(false)} {...props}>
      {children}
    </button>
  )
})
DialogClose.displayName = "DialogClose"

// Centered Modal for both Mobile and Desktop
const DialogContent = React.forwardRef(({ className, children, ...props }, ref) => {
  const { open, onOpenChange } = React.useContext(DialogContext)
  const [mounted, setMounted] = React.useState(false)
  
  React.useEffect(() => {
    setMounted(true)
  }, [])
  
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onOpenChange?.(false)
    }
    if (open) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [open, onOpenChange])
  
  if (!mounted || !open) return null
  
  const modalContent = (
    <>
      {/* Dark Backdrop */}
      <div
        onClick={() => onOpenChange?.(false)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 99998,
        }}
      />
      
      {/* Centered Container */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '16px',
          zIndex: 99999,
        }}
      >
        {/* Modal Box */}
        <div
          ref={ref}
          role="dialog"
          aria-modal="true"
          className={cn(className)}
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            width: '100%',
            maxWidth: '450px',
            maxHeight: 'calc(100vh - 32px)',
            overflowY: 'auto',
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }}
          {...props}
        >
          {children}
          
          {/* Close Button */}
          <button
            onClick={() => onOpenChange?.(false)}
            style={{
              position: 'absolute',
              top: '12px',
              right: '12px',
              background: '#f5f5f5',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              padding: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X style={{ width: '16px', height: '16px', color: '#666' }} />
          </button>
        </div>
      </div>
    </>
  )
  
  return ReactDOM.createPortal(modalContent, document.body)
})
DialogContent.displayName = "DialogContent"

const DialogHeader = ({ className, ...props }) => (
  <div className={cn("mb-4 pr-8", className)} {...props} />
)

const DialogFooter = ({ className, ...props }) => (
  <div className={cn("flex flex-col gap-3 mt-6 sm:flex-row sm:justify-end", className)} {...props} />
)

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <h2 ref={ref} className={cn("text-lg font-semibold text-stone-900", className)} {...props} />
))
DialogTitle.displayName = "DialogTitle"

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <p ref={ref} className={cn("text-sm text-stone-500 mt-1", className)} {...props} />
))
DialogDescription.displayName = "DialogDescription"

export {
  Dialog,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
